import React, { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import styled from 'styled-components';
import { useWindowSize, useWindowScroll, useIntersection } from 'react-use';
import TwoUpModel from '../../models/two-up';
import LinkButton from '../global/linkButton/linkButton';
import Richtext from '../global/richtext/Richtext';
import getBreakpointFlags from '../../helpers/get-breakpoint-flags';
import styles from './two-up.module.scss';
import { generateBlurDataURL } from '../../helpers/image-placeholders';

type Props = {
  data: TwoUpModel;
};

interface CProps {
  backgroundColor?: string;
  descriptionBorder?: string;
  fixTitle?: number | null;
  fontColor?: string;
  paddingTop?: number;
  titleWidth?: number;
}

const CustomStyles = styled.div`
  ${(p: CProps) =>
    p.backgroundColor && `background-color: ${p.backgroundColor};`}
  ${(p: CProps) => p.fontColor && `color: ${p.fontColor};`}
  ${(p: CProps) => p.paddingTop && `padding-top: ${p.paddingTop}px;`}

  .${styles['two-up__item-title-container']} {
    ${(p: CProps) =>
      p.backgroundColor && `background-color: ${p.backgroundColor};`}
    ${(p: CProps) =>
      typeof p.fixTitle === 'number' &&
      `
      bottom: ${p.fixTitle}px;
      position: fixed;
    `}
    ${(p: CProps) => p.titleWidth && `width: ${p.titleWidth}px;`}
  }

  .${styles['two-up__item-description']} {
    ${(p: CProps) =>
      p.descriptionBorder &&
      `border-top: 0.0625rem solid ${p.descriptionBorder};`}
  }
`;

const TwoUp = ({ data }: Props) => {
  if (!data) return <></>;

  const { backgroundColor, separatorColor, items } = data;

  const { width, height } = useWindowSize();
  const { isMobile } = getBreakpointFlags(width);
  const mediaRefs = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null)
  ];
  const titleContainerRefs = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null)
  ];
  const containerRef = useRef<HTMLInputElement>(null);
  const { y } = useWindowScroll();
  const intersection = useIntersection(containerRef, {});
  const [fixTitle, setFixTitle] = useState<number | null>(null);
  const [titleDimensions, setTitleDimensions] = useState({
    height: 0,
    width: 0
  });

  useEffect(() => {
    if (
      mediaRefs[0].current &&
      titleContainerRefs[0].current &&
      intersection?.isIntersecting &&
      !isMobile
    ) {
      const {
        bottom: mediaBottom,
        top: mediaTop,
        width: mediaWidth
      } = mediaRefs[0].current.getBoundingClientRect();
      const { height: titleHeight } =
        titleContainerRefs[0].current.getBoundingClientRect();
      const tittleOffset =
        height - mediaTop < titleHeight
          ? (titleHeight - (height - mediaTop)) * -1
          : 0;

      setTitleDimensions({ height: titleHeight, width: mediaWidth });

      if (mediaBottom + titleHeight < height || mediaTop > height) {
        setFixTitle(null);
      } else {
        setFixTitle(tittleOffset);
      }
    } else {
      setFixTitle(null);
    }
  }, [y, width]);

  return (
    <CustomStyles
      backgroundColor={backgroundColor}
      descriptionBorder={!isMobile ? separatorColor : undefined}
      className={styles['two-up']}
    >
      <div ref={containerRef} className={styles['two-up__items-container']}>
        {items.map((item, index) => (
          <div key={item.title} className={styles['two-up__item']}>
            <div
              ref={mediaRefs[index]}
              className={styles['two-up__item-media']}
            >
              {item.mainMedia && item.mainMedia.isVideo() ? (
                <video
                  tabIndex={0}
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="auto"
                  aria-hidden="true"
                >
                  <source type="video/mp4;" src={item.mainMedia.src} />
                  <p>
                    Sorry, your browser does not support the &lt;video&gt;
                    element.
                  </p>
                </video>
              ) : (
                item.mainMedia && (
                  <Image
                    placeholder="blur"
                    alt={item.mainMedia.alt}
                    height={item.mainMedia.height}
                    src={item.mainMedia.src}
                    width={item.mainMedia.width}
                    layout="responsive"
                    blurDataURL={generateBlurDataURL(item.mainMedia.src)}
                  />
                )
              )}
            </div>
            <CustomStyles
              className={styles['two-up__item-footer']}
              paddingTop={fixTitle !== null ? titleDimensions.height : 0}
              fixTitle={fixTitle}
              titleWidth={fixTitle !== null ? titleDimensions.width : 0}
            >
              {item.title && (
                <div
                  className={styles['two-up__item-title-container']}
                  ref={titleContainerRefs[index]}
                >
                  <CustomStyles
                    fontColor={item.titleColor}
                    className={styles['two-up__item-title']}
                  >
                    {item.title}
                  </CustomStyles>
                  {item.link && !isMobile && (
                    <LinkButton
                      data={item.link}
                      linkClass={styles['two-up__item-link']}
                    />
                  )}
                </div>
              )}
              {item.description && (
                <Richtext
                  customClasses={{
                    container: styles['two-up__item-description']
                  }}
                  data={item.description}
                />
              )}
              {item.link && isMobile && (
                <LinkButton
                  data={item.link}
                  linkClass={styles['two-up__item-link']}
                />
              )}
            </CustomStyles>
          </div>
        ))}
      </div>
    </CustomStyles>
  );
};

export default TwoUp;
